export const PINNED_PDFJS_VERSION = '5.4.296';
export const PINNED_PDFJS_WORKER_ASSET = `static/pdfjs/${PINNED_PDFJS_VERSION}/pdf.worker.min.js`;

export const buildPinnedPdfWorkerUrl = (browserWindow, publicUrl = process.env.PUBLIC_URL || '') => {
  if (!browserWindow?.location?.origin) {
    throw new Error('A browser origin is required for the PDF worker.');
  }
  const normalizedPublicUrl = String(publicUrl).replace(/\/+$/, '');
  const assetPath = `${normalizedPublicUrl}/${PINNED_PDFJS_WORKER_ASSET}`;
  const workerUrl = new URL(assetPath, browserWindow.location.origin);
  if (
    workerUrl.origin !== browserWindow.location.origin
    || workerUrl.search
    || workerUrl.hash
  ) {
    throw new Error('The PDF worker must be a same-origin immutable asset.');
  }
  return workerUrl;
};

export const createPinnedPdfWorkerConfigurator = () => {
  let workerPort = null;
  let terminalFailure = false;

  return (pdfjsApi, browserWindow = window, publicUrl = process.env.PUBLIC_URL || '') => {
    if (terminalFailure) throw new Error('The pinned PDF worker is unavailable.');
    if (pdfjsApi?.version !== PINNED_PDFJS_VERSION || !pdfjsApi?.GlobalWorkerOptions) {
      terminalFailure = true;
      throw new Error('The PDF.js runtime does not match the pinned worker.');
    }
    if (workerPort) {
      pdfjsApi.GlobalWorkerOptions.workerPort = workerPort;
      return workerPort;
    }
    if (typeof browserWindow?.Worker !== 'function') {
      terminalFailure = true;
      throw new Error('A module worker is required to display contract PDFs.');
    }

    const workerUrl = buildPinnedPdfWorkerUrl(browserWindow, publicUrl);
    let candidate;
    try {
      candidate = new browserWindow.Worker(workerUrl.href, {
        type: 'module',
        credentials: 'omit',
        name: `salesbreaker-pdfjs-${PINNED_PDFJS_VERSION}`,
      });
    } catch (_error) {
      terminalFailure = true;
      throw new Error('The pinned PDF worker could not be started.');
    }

    const failClosed = () => {
      terminalFailure = true;
      try { candidate.terminate(); } catch (_error) { /* already unavailable */ }
      workerPort = null;
      // Removing both options prevents PDF.js from switching to an implicit or
      // externally sourced fake-worker implementation after CSP/load failure.
      pdfjsApi.GlobalWorkerOptions.workerPort = null;
      pdfjsApi.GlobalWorkerOptions.workerSrc = '';
    };
    candidate.addEventListener('error', failClosed, { once: true });

    workerPort = candidate;
    try {
      pdfjsApi.GlobalWorkerOptions.workerSrc = workerUrl.href;
      pdfjsApi.GlobalWorkerOptions.workerPort = workerPort;
    } catch (_error) {
      failClosed();
      throw new Error('The pinned PDF worker could not be registered.');
    }
    return workerPort;
  };
};

const configure = createPinnedPdfWorkerConfigurator();

export const configurePinnedPdfWorker = (pdfjsApi, browserWindow = window) => (
  configure(pdfjsApi, browserWindow)
);
